/**
 * VatStatusSection
 * Displays VAT validation status, Peppol registration info,
 * and optionally a side-by-side comparison of DB vs VIES data
 */

import React from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import type { VatValidation, PeppolRegistration } from '../../../types/facturation.types';
import { VatDataComparison } from './VatDataComparison';

/**
 * Check if a VAT number is valid (not empty, not "NA", not "N/A", etc.)
 */
const isValidVatNumber = (vatNumber: string | null | undefined): boolean => {
  if (!vatNumber || typeof vatNumber !== 'string') return false;
  const cleaned = vatNumber.trim().toUpperCase();
  return cleaned !== '' && cleaned !== 'NA' && cleaned !== 'N/A' && cleaned !== '-' && cleaned !== 'NÉANT';
};

interface BillingContactData {
  name: string | null;
  address?: string | null;
  vat_number?: string | null;
  statut_juridique?: string | null;
  statut_tva?: string | null;
}

interface VatStatusSectionProps {
  vatNumber?: string | null;
  isValidating: boolean;
  vatValidationResult: VatValidation | null;
  peppolStatus?: PeppolRegistration | null;
  /** Optional: billing contact data for detailed comparison view */
  billingContact?: BillingContactData | null;
  /** Show detailed comparison view (default: false for backward compatibility) */
  showDetailedComparison?: boolean;
}

export const VatStatusSection: React.FC<VatStatusSectionProps> = ({
  vatNumber,
  isValidating,
  vatValidationResult,
  peppolStatus,
  billingContact,
  showDetailedComparison = false
}) => {
  const hasVatNumber = isValidVatNumber(vatNumber);

  return (
    <>
      {/* VAT Validation in progress */}
      {hasVatNumber && isValidating && (
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 flex-shrink-0"></div>
            <div>
              <h3 className="font-medium text-blue-900">Validation TVA en cours...</h3>
              <p className="text-sm text-blue-700 mt-1">
                Verification du numero de TVA {vatNumber} aupres du service VIES.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* VAT Validation error - Invalid VAT (blocking)
          Note: This is intentionally not displayed here.
          The parent component handles this with an override checkbox option.
      */}

      {/* VAT Validation error (rate limit or API error) */}
      {vatValidationResult && (vatValidationResult.errorType === 'RATE_LIMIT' || vatValidationResult.errorType === 'API_ERROR') && (
        <div className="bg-yellow-50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <InformationCircleIcon className="h-6 w-6 text-yellow-600 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-yellow-900">
                {vatValidationResult.errorType === 'RATE_LIMIT'
                  ? 'Service VIES temporairement indisponible'
                  : 'Erreur de validation TVA'}
              </h3>
              <p className="text-sm text-yellow-700 mt-1">
                {vatValidationResult.error}
              </p>
              <p className="text-xs text-yellow-600 mt-2">
                Vous pouvez continuer la generation de facture. La validation TVA peut etre effectuee ulterieurement.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* VAT Validation warning - needs review but valid */}
      {vatValidationResult && vatValidationResult.isValid && vatValidationResult.needsReview && !vatValidationResult.errorType && (
        <div className="bg-yellow-50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-yellow-900">Verification TVA requise</h3>
              <p className="text-sm text-yellow-700 mt-1">
                Les donnees officielles VIES different de celles enregistrees.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* VAT Validation success */}
      {hasVatNumber && vatValidationResult && vatValidationResult.isValid && !vatValidationResult.needsReview && !vatValidationResult.errorType && (
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-green-900">TVA valide</h3>
              <p className="text-sm text-green-700 mt-1">
                Le numero de TVA <span className="font-mono">{vatNumber}</span> a ete verifie avec succes.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Peppol status */}
      {peppolStatus && (
        <div className={`rounded-lg p-4 ${peppolStatus.peppolRegistered ? 'bg-blue-50' : 'bg-gray-50'}`}>
          <div className="flex items-start gap-3">
            <GlobeAltIcon className={`h-6 w-6 ${peppolStatus.peppolRegistered ? 'text-blue-600' : 'text-gray-400'} flex-shrink-0`} />
            <div>
              <h3 className={`font-medium ${peppolStatus.peppolRegistered ? 'text-blue-900' : 'text-gray-700'}`}>
                {peppolStatus.peppolRegistered ? 'Peppol actif' : 'Peppol non disponible'}
              </h3>
              <p className={`text-sm mt-1 ${peppolStatus.peppolRegistered ? 'text-blue-700' : 'text-gray-500'}`}>
                {peppolStatus.peppolRegistered
                  ? 'La facture sera envoyee via le reseau Peppol.'
                  : 'La facture sera envoyee par email.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Detailed VAT Data Comparison (optional) */}
      {showDetailedComparison && billingContact && hasVatNumber && !isValidating && (
        <VatDataComparison
          billingContact={billingContact}
          vatValidationResult={vatValidationResult}
        />
      )}
    </>
  );
};

export default VatStatusSection;
